import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Hero() {
  const { t } = useTranslation('tole');
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.1);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-[85vh] md:min-h-[90vh] flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Stunning Quebec heritage house with dark slate blue standing seam metal roof, warm golden hour sunlight, crisp architectural lines, beautiful manicured garden, dramatic sky, high-end real estate photography, no text visible, warm copper and slate tones, editorial magazine quality, wide angle landscape composition"
          alt="Maison québécoise avec toiture tôle ardoise premium à Lévis par couvreur certifié RBQ"
          className="w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
          width="1920"
          height="1080"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-primary/50 via-slate-primary/30 to-slate-primary/70" />
      </div>

      {/* Content */}
      <div
        ref={ref}
        className={`relative z-10 w-full px-4 md:px-6 lg:px-10 pt-24 pb-20 max-w-5xl mx-auto text-center transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Breadcrumb */}
        <nav className="mb-6" aria-label="Fil d'Ariane">
          <ol className="flex items-center justify-center gap-2 text-sm text-white/70">
            <li>
              <button
                onClick={() => {
                  if (window.REACT_APP_NAVIGATE) window.REACT_APP_NAVIGATE('/');
                }}
                className="hover:text-white transition-colors"
              >
                {t('breadcrumb_home')}
              </button>
            </li>
            <li><i className="ri-arrow-right-s-line" /></li>
            <li className="text-white font-medium">{t('breadcrumb_current')}</li>
          </ol>
        </nav>

        {/* H1 */}
        <h1 className="font-manrope font-extrabold text-white text-[40px] sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-5">
          {t('hero_h1')}
        </h1>

        {/* Subtitle */}
        <p className="text-white/85 text-base md:text-lg lg:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
          {t('hero_subtitle')}
        </p>

        {/* CTA */}
        <button
          onClick={() => scrollToSection('quote-form')}
          className="group bg-copper hover:bg-copper-dark text-white font-semibold px-8 py-4 rounded-md text-base md:text-lg transition-all duration-300 flex items-center gap-3 mx-auto whitespace-nowrap"
        >
          {t('hero_cta')}
          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
        </button>

        {/* Stats */}
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-10">
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <i className="ri-time-line text-copper" />
            <span className="text-white text-sm font-medium">{t('hero_stat_1')}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <i className="ri-file-shield-line text-copper" />
            <span className="text-white text-sm font-medium">{t('hero_stat_2')}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <i className="ri-line-chart-line text-copper" />
            <span className="text-white text-sm font-medium">{t('hero_stat_3')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}